/**
 * Installations API Service
 *
 * Handles all installation-related API calls.
 * Matches FastAPI backend with nested location/configuration/installation_metadata.
 */

import { api } from '../client';

export const installationsService = {
  /**
   * Get all installations
   * @returns {Promise<Array>} Array of all installations
   */
  async getAll() {
    const response = await api.post('/api/installation', {});
    return response || [];
  },

  /**
   * Get installations by IDs
   * @param {Array<string>} installationIds - Array of installation IDs
   * @returns {Promise<Array>} Array of installations
   */
  async getByIds(installationIds) {
    if (!installationIds || installationIds.length === 0) {
      return [];
    }

    const response = await api.post('/api/installation', {
      _id: { $in: installationIds },
    });

    return response || [];
  },

  /**
   * Get a single installation by ID
   * @param {string} installationId - Installation ID
   * @returns {Promise<Object>} Installation details
   */
  async getById(installationId) {
    const response = await api.post('/api/installation', {
      _id: installationId,
    });

    return response[0]; // API returns array
  },

  /**
   * Create a new installation
   * @param {Object} installationData - Installation data with nested objects
   * @returns {Promise<Object>} Created installation
   */
  async create(installationData) {
    return await api.put('/api/installation', installationData);
  },

  /**
   * Update an existing installation
   * @param {string} installationId - Installation ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated installation
   */
  async update(installationId, updates) {
    return await api.patch('/api/installation', {
      query: { _id: installationId },
      update_data: updates,
    });
  },

  /**
   * Delete an installation
   * @param {string} installationId - Installation ID
   * @returns {Promise<Object>} Deletion response
   */
  async delete(installationId) {
    return await api.delete('/api/installation', {
      data: { _id: installationId },
    });
  },
};
